import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Crown,
  Zap,
  Check,
  Sparkles,
  CreditCard,
  Loader2,
  Users,
  Shield,
  Star,
  ArrowRight,
  Gift,
  Clock,
  Infinity,
  ChevronRight,
  BadgeCheck,
  Rocket,
  Building2,
  Package,
  Flame,
  Timer,
  FileText,
  Download,
  ExternalLink,
  Receipt
} from 'lucide-react';

// ==================== COUNTDOWN TIMER HOOK ====================
const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    if (!targetDate) return;
    
    const calculateTimeLeft = () => {
      const now = new Date();
      const target = new Date(targetDate);
      const diff = target - now;
      
      if (diff <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
      }
      
      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
        expired: false
      };
    };
    
    setTimeLeft(calculateTimeLeft());
    
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearInterval(timer);
  }, [targetDate]);
  
  return timeLeft;
};

// ==================== FOUNDERS PROMO BANNER ====================
const FoundersPromoBanner = ({ promo }) => {
  const timeLeft = useCountdown(promo?.end_date);
  
  if (!promo?.active || timeLeft.expired) return null;
  
  return (
    <div className="mb-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 via-red-500/20 to-purple-500/20 animate-pulse" />
      <div className="relative p-6 rounded-2xl border-2 border-orange-500/50 bg-gradient-to-r from-orange-500/10 via-red-500/10 to-purple-500/10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Left: Promo Info */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
              <Flame className="w-8 h-8 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-3 py-1 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-bold">
                  {promo.badge_text}
                </span>
                <span className="text-xs text-orange-400 font-medium">OFERTA LIMITADA</span>
              </div>
              <h3 className="text-xl font-bold text-text-main">
                {promo.discount_text}
              </h3>
              <p className="text-sm text-text-secondary">
                Acceso de fundador con precios especiales para siempre
              </p>
            </div>
          </div>
          
          {/* Right: Countdown */}
          <div className="flex items-center gap-2">
            <Timer className="w-5 h-5 text-orange-400" />
            <span className="text-sm text-text-muted mr-2">Termina en:</span>
            <div className="flex gap-2">
              {[
                { value: timeLeft.days, label: 'd' },
                { value: timeLeft.hours, label: 'h' },
                { value: timeLeft.minutes, label: 'm' },
                { value: timeLeft.seconds, label: 's' }
              ].map((unit) => (
                <div key={unit.label} className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-lg bg-surface border border-orange-500/30 flex items-center justify-center">
                    <span className="text-xl font-bold text-orange-400">
                      {String(unit.value).padStart(2, '0')}
                    </span>
                  </div>
                  <span className="text-[10px] text-text-muted mt-1">{unit.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== PLAN CARD COMPONENT ====================
const PlanCard = ({ 
  plan, 
  planId,
  isCurrentPlan, 
  billingCycle, 
  onSelectPlan, 
  isLoading,
  isFounderPrice
}) => {
  const price = billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price;
  const originalPrice = billingCycle === 'yearly' ? plan.original_yearly_price : plan.original_monthly_price;
  const monthlyEquivalent = billingCycle === 'yearly' ? Math.round(plan.yearly_price / 12) : plan.monthly_price;
  const savings = billingCycle === 'yearly' && plan.monthly_price > 0
    ? Math.round((plan.monthly_price * 12 - plan.yearly_price) / (plan.monthly_price * 12) * 100)
    : 0;
  
  const isAgencia = planId === 'agencia';
  const isCreador = planId === 'creador';
  const showFounderDiscount = isFounderPrice && price < originalPrice;
  const discountPercent = showFounderDiscount ? Math.round((1 - price / originalPrice) * 100) : 0;
  
  return (
    <div 
      className={`relative rounded-2xl border-2 p-6 transition-all ${
        isCurrentPlan 
          ? 'border-green-500 bg-green-500/5 ring-2 ring-green-500/20' 
          : showFounderDiscount
            ? 'border-orange-500 bg-gradient-to-b from-orange-500/10 to-transparent ring-2 ring-orange-500/20'
            : isAgencia
              ? 'border-brand-500 bg-brand/5 ring-2 ring-brand/20'
              : 'border-border-subtle bg-surface hover:border-brand/30'
      }`}
      data-testid={`plan-card-${planId}`}
    >
      {/* Current Plan Badge */}
      {isCurrentPlan && (
        <div className="absolute -top-3 left-4 px-3 py-1 rounded-full bg-green-500 text-white text-xs font-bold">
          Tu Plan Actual
        </div>
      )}
      
      {/* Founder Discount Badge */}
      {showFounderDiscount && !isCurrentPlan && (
        <div className="absolute -top-3 left-4 px-3 py-1 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold flex items-center gap-1 animate-pulse">
          <Flame className="w-3 h-3" />
          FOUNDERS -{discountPercent}%
        </div>
      )}
      
      {/* Popular Badge (only if not founder discount) */}
      {isAgencia && !isCurrentPlan && !showFounderDiscount && (
        <div className="absolute -top-3 left-4 px-3 py-1 rounded-full bg-brand-500 text-white text-xs font-bold flex items-center gap-1">
          <Star className="w-3 h-3" />
          Más Popular
        </div>
      )}
      
      {/* Savings Badge */}
      {savings > 0 && (
        <div className="absolute -top-3 right-4 px-3 py-1 rounded-full bg-amber-500 text-white text-xs font-bold">
          Ahorra {savings}%
        </div>
      )}
      
      {/* Plan Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            showFounderDiscount ? 'bg-orange-500/20' : isAgencia ? 'bg-brand/20' : isCreador ? 'bg-purple-500/20' : 'bg-gray-500/20'
          }`}>
            {isAgencia ? (
              <Building2 className={`w-6 h-6 ${showFounderDiscount ? 'text-orange-400' : 'text-brand'}`} />
            ) : isCreador ? (
              <Rocket className={`w-6 h-6 ${showFounderDiscount ? 'text-orange-400' : 'text-purple-400'}`} />
            ) : (
              <Users className="w-6 h-6 text-gray-400" />
            )}
          </div>
          <div>
            <h3 className={`text-xl font-bold ${showFounderDiscount ? 'text-orange-400' : isAgencia ? 'text-brand' : 'text-text-main'}`}>
              {plan.name}
            </h3>
          </div>
        </div>
        
        {/* Price */}
        <div className="mt-4">
          <div className="flex items-baseline gap-2">
            {/* Strikethrough original price */}
            {showFounderDiscount && (
              <span className="text-xl text-text-muted line-through">
                €{originalPrice}
              </span>
            )}
            <span className={`text-4xl font-bold ${showFounderDiscount ? 'text-orange-400' : 'text-text-main'}`}>
              {price === 0 ? 'Gratis' : `€${price}`}
            </span>
            {price > 0 && (
              <span className="text-text-muted">
                / {billingCycle === 'yearly' ? 'año' : 'mes'}
              </span>
            )}
          </div>
          {billingCycle === 'yearly' && price > 0 && (
            <p className="text-sm text-text-muted mt-1">
              Equivalente a €{monthlyEquivalent}/mes
            </p>
          )}
          {showFounderDiscount && (
            <p className="text-xs text-orange-400 mt-1 font-medium">
              💎 Precio bloqueado para siempre
            </p>
          )}
        </div>
      </div>
      
      {/* Features */}
      <div className="space-y-3 mb-6">
        {plan.features.map((feature) => (
          <div key={feature} className="flex items-center gap-2">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
              showFounderDiscount ? 'bg-orange-500/20' : isAgencia ? 'bg-brand/20' : 'bg-green-500/20'
            }`}>
              <Check className={`w-3 h-3 ${showFounderDiscount ? 'text-orange-400' : isAgencia ? 'text-brand' : 'text-green-400'}`} />
            </div>
            <span className="text-sm text-text-secondary">{feature}</span>
          </div>
        ))}
        
        {/* Credits highlight */}
        <div className="flex items-center gap-2 pt-2 border-t border-border-subtle">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <span className="text-sm font-medium text-amber-400">
            {plan.credits_monthly?.toLocaleString() || plan.monthly_credits?.toLocaleString()} créditos/mes incluidos
          </span>
        </div>
      </div>
      
      {/* CTA Button */}
      {isCurrentPlan ? (
        <Button
          disabled
          className="w-full bg-green-500/20 text-green-400 cursor-default"
        >
          <BadgeCheck className="w-4 h-4 mr-2" />
          Plan Activo
        </Button>
      ) : (
        <Button
          onClick={() => onSelectPlan(planId)}
          disabled={isLoading || planId === 'free'}
          className={`w-full ${
            showFounderDiscount
              ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white'
              : isAgencia 
                ? 'bg-brand-500 hover:bg-brand-hover text-white' 
                : 'bg-purple-500 hover:bg-purple-600 text-white'
          }`}
          data-testid={`subscribe-btn-${planId}`}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : showFounderDiscount ? (
            <Flame className="w-4 h-4 mr-2" />
          ) : (
            <Crown className="w-4 h-4 mr-2" />
          )}
          {planId === 'free' ? 'Plan Actual' : showFounderDiscount ? '¡Unirme como Founder!' : 'Suscribirse'}
        </Button>
      )}
      
      {/* 🎯 FOUNDER PRE-SALE NOTICE */}
      {showFounderDiscount && planId !== 'free' && !isCurrentPlan && (
        <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <Clock className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-emerald-300">
              <strong>Precio Founder</strong> · No se te cobra hasta el <strong>31 de julio</strong> · Acceso disponible el 31 de julio de 2026
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== CREDIT PACK CARD COMPONENT ====================
const CreditPackCard = ({ pack, packId, onBuy, isLoading }) => {
  return (
    <div 
      className={`relative rounded-xl border-2 p-5 transition-all hover:border-brand/50 ${
        pack.popular 
          ? 'border-amber-500/50 bg-amber-500/5' 
          : 'border-border-subtle bg-surface'
      }`}
      data-testid={`credit-pack-${packId}`}
    >
      {pack.popular && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-amber-500 text-white text-xs font-bold">
          Mejor Valor
        </div>
      )}
      
      <div className="text-center">
        <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-3 ${
          pack.popular ? 'bg-amber-500/20' : 'bg-brand/10'
        }`}>
          <Package className={`w-7 h-7 ${pack.popular ? 'text-amber-400' : 'text-brand'}`} />
        </div>
        
        <h4 className="font-bold text-lg text-text-main">{pack.name}</h4>
        
        <div className="my-3">
          <span className="text-3xl font-bold text-text-main">
            {pack.credits.toLocaleString()}
          </span>
          <span className="text-text-muted ml-1">créditos</span>
        </div>
        
        <div className="text-2xl font-bold text-brand-500 mb-1">
          €{pack.price}
        </div>
        
        <p className="text-xs text-text-muted mb-4">
          €{(pack.price / pack.credits * 100).toFixed(1)} por 100 créditos
        </p>
        
        <Button
          onClick={() => onBuy(packId)}
          disabled={isLoading}
          variant="outline"
          className={`w-full ${pack.popular ? 'border-amber-500/50 text-amber-400 hover:bg-amber-500/10' : ''}`}
          data-testid={`buy-pack-btn-${packId}`}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Zap className="w-4 h-4 mr-2" />
          )}
          Comprar Pack
        </Button>
      </div>
      
      {/* Non-expiring badge */}
      <div className="mt-3 pt-3 border-t border-border-subtle">
        <div className="flex items-center justify-center gap-1 text-xs text-green-400">
          <Infinity className="w-3.5 h-3.5" />
          <span>Créditos permanentes (no caducan)</span>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN BILLING PAGE ====================
const Billing = () => {
  const { api, user, refreshUser } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState(null);
  const [packs, setPacks] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [foundersPromo, setFoundersPromo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [buyingPack, setBuyingPack] = useState(null);
  
  // Invoice states
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);

  // Load invoices
  const loadInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const res = await api.get('/billing/invoices');
      setInvoices(res.data.invoices || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Error al cargar facturas');
    } finally {
      setLoadingInvoices(false);
    }
  };

  // Load billing data
  useEffect(() => {
    const loadBillingData = async () => {
      try {
        const [plansRes, statusRes] = await Promise.all([
          api.get('/billing/plans'),
          api.get('/billing/status')
        ]);
        
        setPlans(plansRes.data.subscription_plans);
        setPacks(plansRes.data.credit_packs);
        setFoundersPromo(plansRes.data.founders_promo);
        setBillingStatus(statusRes.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Error loading billing data:', error);
        toast.error('Error al cargar datos de facturación');
      } finally {
        setLoading(false);
      }
    };
    
    loadBillingData();
  }, [api]);

  // Subscribe to plan
  const handleSubscribe = async (planId) => {
    setSubscribing(planId);
    
    try {
      // Simulate payment delay
      toast.info('Procesando pago...', { duration: 2000 });
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const response = await api.post('/billing/subscribe', {
        plan: planId,
        billing_cycle: billingCycle
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Refresh billing status
        const statusRes = await api.get('/billing/status');
        setBillingStatus(statusRes.data);
        
        // Refresh user context
        if (refreshUser) {
          await refreshUser();
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Subscription error:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar la suscripción');
    } finally {
      setSubscribing(null);
    }
  };

  // Buy credit pack
  const handleBuyPack = async (packId) => {
    setBuyingPack(packId);
    
    try {
      // Simulate payment delay
      toast.info('Procesando compra...', { duration: 2000 });
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const response = await api.post('/billing/buy-credits', {
        pack_id: packId
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Refresh billing status
        const statusRes = await api.get('/billing/status');
        setBillingStatus(statusRes.data);
        
        // Refresh user context
        if (refreshUser) {
          await refreshUser();
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar la compra');
    } finally {
      setBuyingPack(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-brand-500 mx-auto mb-4" />
          <p className="text-text-muted">Cargando planes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app-bg py-12 px-4" data-testid="billing-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand/10 text-brand-500 text-sm font-medium mb-4">
            <CreditCard className="w-4 h-4" />
            Precios y Facturación
          </div>
          <h1 className="text-4xl font-bold text-text-main mb-3">
            Elige tu Plan
          </h1>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Desbloquea todo el potencial de FascinAI ONE con nuestros planes diseñados para creadores y agencias de marketing.
          </p>
        </div>
        
        {/* FOUNDERS PROMO BANNER */}
        {foundersPromo && <FoundersPromoBanner promo={foundersPromo} />}
        
        {/* Current Balance Banner */}
        {billingStatus && (
          <div className="mb-8 p-6 bg-gradient-to-r from-brand/10 via-purple-500/10 to-amber-500/10 rounded-2xl border border-brand/20">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm text-text-muted mb-1">Tu saldo actual</p>
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-bold text-text-main">
                    {billingStatus.credits_balance.toLocaleString()}
                  </span>
                  <span className="text-2xl">🪙</span>
                </div>
                <div className="flex items-center gap-4 mt-2 text-sm text-text-muted">
                  <span>De suscripción: {billingStatus.credits_from_subscription.toLocaleString()}</span>
                  <span>•</span>
                  <span className="text-green-400">Permanentes: {billingStatus.credits_from_packs.toLocaleString()}</span>
                </div>
              </div>
              
              <div className="text-right">
                <p className="text-sm text-text-muted mb-1">Proyectos</p>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-text-main">
                    {billingStatus.projects_count}
                  </span>
                  <span className="text-text-muted">
                    / {billingStatus.project_limit === -1 ? '∞' : billingStatus.project_limit}
                  </span>
                </div>
                <p className={`text-xs mt-1 ${billingStatus.can_create_project ? 'text-green-400' : 'text-red-400'}`}>
                  {billingStatus.can_create_project ? 'Puedes crear proyectos' : 'Límite alcanzado'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center p-1 bg-surface rounded-xl border border-border-subtle">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-brand-500 text-white'
                  : 'text-text-muted hover:text-text-main'
              }`}
              data-testid="toggle-monthly"
            >
              Mensual
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'yearly'
                  ? 'bg-brand-500 text-white'
                  : 'text-text-muted hover:text-text-main'
              }`}
              data-testid="toggle-yearly"
            >
              Anual
              <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs">
                -15%
              </span>
            </button>
          </div>
        </div>
        
        {/* ==================== SECTION A: SUBSCRIPTION PLANS ==================== */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-text-main mb-2">Planes de Suscripción</h2>
            <p className="text-text-muted">Créditos mensuales incluidos + funciones premium</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans && Object.entries(plans).map(([planId, plan]) => (
              <PlanCard
                key={planId}
                planId={planId}
                plan={plan}
                isCurrentPlan={billingStatus?.subscription_plan === planId}
                billingCycle={billingCycle}
                onSelectPlan={handleSubscribe}
                isLoading={subscribing === planId}
                isFounderPrice={plan.is_founder_price}
              />
            ))}
          </div>
        </section>
        
        {/* ==================== SECTION B: CREDIT PACKS ==================== */}
        <section>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 text-green-400 text-sm font-medium mb-3">
              <Gift className="w-4 h-4" />
              Créditos Extra (One-time)
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-2">Packs de Recarga</h2>
            <p className="text-text-muted">Créditos adicionales que nunca caducan</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {packs && Object.entries(packs).map(([packId, pack]) => (
              <CreditPackCard
                key={packId}
                packId={packId}
                pack={pack}
                onBuy={handleBuyPack}
                isLoading={buyingPack === packId}
              />
            ))}
          </div>
        </section>
        
        {/* FAQ / Info Section */}
        <section className="mt-16 max-w-3xl mx-auto">
          <div className="bg-surface rounded-2xl border border-border-subtle p-6">
            <h3 className="font-bold text-lg text-text-main mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-brand-500" />
              Información Importante
            </h3>
            
            <div className="space-y-4 text-sm text-text-secondary">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-text-main">Créditos de Suscripción</p>
                  <p>Se recargan automáticamente cada mes según tu plan. No se acumulan.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Infinity className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-text-main">Créditos de Packs</p>
                  <p>Los créditos comprados con packs son <span className="text-green-400 font-medium">permanentes</span> y nunca caducan.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-purple-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-text-main">Consumo de Créditos</p>
                  <p>Primero se usan los créditos de suscripción, luego los de packs.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Invoices Section */}
        <section className="mt-16 max-w-4xl mx-auto" data-testid="invoices-section">
          <div className="bg-surface rounded-2xl border border-border-subtle p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Receipt className="w-6 h-6 text-brand-500" />
                <div>
                  <h3 className="font-bold text-lg text-text-main">Historial de Facturas</h3>
                  <p className="text-sm text-text-muted">Descarga tus facturas con el ID oficial de Stripe</p>
                </div>
              </div>
              <Button
                onClick={() => {
                  setShowInvoices(!showInvoices);
                  if (!showInvoices && invoices.length === 0) {
                    loadInvoices();
                  }
                }}
                variant="outline"
                size="sm"
                data-testid="toggle-invoices-btn"
              >
                {showInvoices ? 'Ocultar' : 'Ver Facturas'}
                <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${showInvoices ? 'rotate-90' : ''}`} />
              </Button>
            </div>
            
            {showInvoices && (
              <div className="mt-4">
                {loadingInvoices ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
                    <span className="ml-2 text-text-muted">Cargando facturas...</span>
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center py-8 text-text-muted">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay facturas disponibles</p>
                    <p className="text-sm mt-1">Las facturas aparecerán aquí después de tu primer pago</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {invoices.map((invoice) => (
                      <div 
                        key={invoice.id}
                        className="flex items-center justify-between p-4 bg-bg-main rounded-lg border border-border-subtle hover:border-brand-500/30 transition-colors"
                        data-testid={`invoice-${invoice.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-brand-500/10 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-brand-500" />
                          </div>
                          <div>
                            <p className="font-medium text-text-main">
                              {invoice.number || invoice.id}
                            </p>
                            <p className="text-sm text-text-muted">
                              {new Date(invoice.date).toLocaleDateString('es-ES', { 
                                day: 'numeric', 
                                month: 'long', 
                                year: 'numeric' 
                              })}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-bold text-text-main">
                              {invoice.amount.toFixed(2)} {invoice.currency}
                            </p>
                            <p className="text-xs text-green-400 flex items-center justify-end gap-1">
                              <Check className="w-3 h-3" />
                              Pagada
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {invoice.pdf_url && (
                              <a
                                href={invoice.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 rounded-lg bg-brand-500/10 hover:bg-brand-500/20 text-brand-500 transition-colors"
                                title="Descargar PDF"
                                data-testid={`download-invoice-${invoice.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </a>
                            )}
                            {invoice.hosted_url && (
                              <a
                                href={invoice.hosted_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 rounded-lg bg-gray-500/10 hover:bg-gray-500/20 text-text-muted transition-colors"
                                title="Ver en Stripe"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Info about Stripe Invoice ID */}
                <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-xs text-blue-400 flex items-start gap-2">
                    <Shield className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      Las facturas descargadas incluyen el <strong>ID oficial de Stripe</strong> válido para tu contabilidad y cumplimiento fiscal.
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Billing;
